/**
 * MapLabels
 * Renders place markers, labels, clusters, zoom zones, and unit labels as HTML elements
 * This provides crisp rendering at any zoom level
 */

import React, {
  useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef,
} from 'react';
import { useAppStore, useMapStateStore, useMapInteractionStore } from '../../../stores';
import { useFavoriteButtons } from '../../Personalization/Favorite/FavoriteButtonsContext';
import { svgToPercent, getCenterFromPlace } from './MapLabelsHelpers';
import MapPlaceLabel from '../MapPlace/MapPlaceLabel';
import MapPlaceCluster from '../MapPlace/MapPlaceCluster';
import MapUnitLabel from '../MapUnit/MapUnitLabel';
import MapZoomZone from '../MapZoomZone/MapZoomZone';
/**
 * Main HTML Overlay Component
 */
const MapLabels = ({
  places = [],
  clusters = [],
  zoomZones = {},
  zoneFootprints = null,
  units = [],
  unitsVersion = 0,
  unclusteredPlaces = [],
  clusteringEnabled = false,
  showZoomZones = false,
  zoomZonesOnFloor = false,
  showUnitLabels = false,
  showPlaceLabels = true,
  platformShowOccupancy = false,
  platformShowMapPlaceLabelsInitially = false,
  platformShowLowerValuePlaceLabelsInitially = false,
  isZoomZonesGuideActive = false,
  isAmenitiesGuideActive = false,
  viewBox,
  activeZoom,
  baseZoom = 18,
  zIndex = 1,
  onMarkerClick,
  onClusterClick,
  onZoomZoneClick,
  onOverflowClusterClick,
  debugBounds = null,
  debugClusterBounds = null,
  isPreviewMode = false,
}) => {
  // Track which non-link labels are visible
  const [visibleLabels, setVisibleLabels] = useState({});

  // Per-zone orientation override resolved by the collision-detection pass.
  // Keys are `zone:${uuid|tid|id}`; values are 'top' | 'bottom' | 'left' | 'right'.
  const [zoneOrientationOverrides, setZoneOrientationOverrides] = useState({});
  // Per-zone icon offset (px) applied when the zone's dot would collide with a
  // place dot. Shifts the whole zoom zone (icon + label) by `{ dx, dy }`.
  const [zoneIconOffsets, setZoneIconOffsets] = useState({});
  // Overflow clustering disabled per user request — zoom zone labels just
  // cover place labels directly rather than hiding them or creating clusters.
  // State retained for potential future use but never populated.
  const [overflowZones] = useState({});
  // Counter bumped by a document-level MutationObserver so the collision
  // pass re-runs when label DOM changes in a sibling MapLabels (e.g. when
  // place labels are minimized or opened).
  const [collisionTick, setCollisionTick] = useState(0);
  const containerRef = useRef(null);

  // Get personalization settings from Zustand
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const settings = useAppStore((state) => state.settings);
  const setCoveredPlaceUuids = useMapStateStore((state) => state.setCoveredPlaceUuids);
  const setClusterBoosts = useMapStateStore((state) => state.setClusterBoosts);
  const clusterBoosts = useMapStateStore((state) => state.clusterBoosts);
  const activeLevelOfCareUuid = useMapInteractionStore(
    (state) => state.activeLevelOfCareUuid,
  );
  const { platform } = settings || {};

  // Get raw favorites data for pre-computing favorited UUIDs
  const { favorites, residenceFavorites } = useFavoriteButtons();

  // Heart icons show when personalization is active OR on page platform, and item is favorited
  const showHeartEnabled = personalizationActive || platform === 'page';

  // Pre-compute favorited UUIDs as Sets for O(1) lookup
  const favoritedUnitUuids = useMemo(() => {
    if (!showHeartEnabled) return new Set();
    return new Set(residenceFavorites?.residence || []);
  }, [showHeartEnabled, residenceFavorites?.residence]);

  const favoritedPlaceUuids = useMemo(() => {
    if (!showHeartEnabled) return new Set();
    return new Set(favorites?.place || []);
  }, [showHeartEnabled, favorites?.place]);

  const toggleLabel = useCallback((uuid) => {
    setVisibleLabels((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));
  }, []);

  // Stable signature of ZONE label visibility only. The orientation effect
  // uses this instead of the full visibleLabels object so toggling an
  // individual place label doesn't re-run the collision pass. Computed as
  // a sorted string so React's Object.is comparison short-circuits when
  // only place keys changed.
  const zoneVisibilitySignature = Object.entries(visibleLabels)
    .filter(([k]) => k.startsWith('zone:'))
    .map(([k, v]) => `${k}:${v ? 1 : 0}`)
    .sort()
    .join(',');

  // Determine which places to render (clustered vs unclustered)
  const placesToRender = clusteringEnabled ? unclusteredPlaces : places;

  // Seed zoom zone labels to "visible" so the user can minimize them via a
  // click toggle (mirroring non-link place labels). Only `--asDot` zones use
  // this toggle; full zoom zones ignore the state and always render their
  // label.
  useEffect(() => {
    if (!zoomZones) return;
    setVisibleLabels((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.values(zoomZones).forEach((zone) => {
        const key = `zone:${zone.uuid || zone.tid || zone.id}`;
        if (next[key] === undefined) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [zoomZones]);

  // Seed initial place label visibility from feature flags.
  // "Show Map Place Labels Initially" controls link labels (places with modals/media).
  // "Show Lower Value Map Place Labels Initially" controls non-link labels (places without modals).
  // Link labels (create_modal, link_url, modal_nid) always render via the isLink check in JSX,
  // so the seeding here only affects non-link places.
  // Merges into existing state so user-toggled labels aren't lost on re-renders.
  useEffect(() => {
    if (!platformShowMapPlaceLabelsInitially || !placesToRender.length) {
      return;
    }
    if (!platformShowLowerValuePlaceLabelsInitially) return;

    setVisibleLabels((prev) => {
      const next = { ...prev };
      let changed = false;
      placesToRender.forEach((place, index) => {
        const pin = place?.pin || {};
        const isLink = pin.is_link !== false && (pin.link_url || pin.modal_nid || pin.create_modal);
        if (isLink) return;
        const uniqueId = place.uuid || pin.uuid || `place-${index}`;
        // Only seed new labels — never overwrite user-toggled state
        if (next[uniqueId] === undefined) {
          next[uniqueId] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [platformShowMapPlaceLabelsInitially,
    platformShowLowerValuePlaceLabelsInitially,
    placesToRender]);

  // Memoize position calculations
  const markerPositions = useMemo(() => {
    if (!viewBox) return [];

    return placesToRender.map((place, index) => {
      const center = getCenterFromPlace(place);
      if (!center) return null;

      const position = svgToPercent(center.x, center.y, viewBox);
      if (!position) return null;

      const pin = place?.pin || {};
      const isLink = pin.is_link !== false && (pin.link_url || pin.modal_nid || pin.create_modal);

      const uniqueId = place.uuid || pin.uuid || `place-${index}`;

      return {
        place,
        position,
        isLink,
        uniqueId,
      };
    }).filter(Boolean);
  }, [placesToRender, viewBox]);

  const clusterPositions = useMemo(() => {
    if (!clusteringEnabled || !viewBox) return [];

    return clusters.map((cluster) => {
      if (!cluster.center) return null;

      const position = svgToPercent(cluster.center.x, cluster.center.y, viewBox);
      if (!position) return null;

      return { cluster, position };
    }).filter(Boolean);
  }, [clusters, clusteringEnabled, viewBox]);

  const zoomZonePositions = useMemo(() => {
    if (!showZoomZones || !viewBox) return [];

    return Object.entries(zoomZones).map(([key, zone]) => {
      if (!zone.center) return null;

      const position = svgToPercent(zone.center.x, zone.center.y, viewBox);
      if (!position) return null;

      return {
        zoomZone: { ...zone, id: zone.id || key },
        position,
      };
    }).filter(Boolean);
  }, [zoomZones, showZoomZones, viewBox]);

  // Calculate unit label positions
  const unitLabelPositions = useMemo(() => {
    if (!showUnitLabels || !viewBox || !units.length) return [];

    return units
      .filter((unit) => {
        if (unit.displayMode === 'disabled') return false;
        const { x, y } = unit.label || {};
        return x !== undefined && y !== undefined;
      })
      .map((unit) => {
        const { x, y } = unit.label;
        const svgX = parseFloat(x);
        const svgY = parseFloat(y);

        if (Number.isNaN(svgX) || Number.isNaN(svgY)) return null;

        const position = svgToPercent(svgX, svgY, viewBox);
        if (!position) return null;

        const uniqueId = unit.info?.uuid || unit.attributes?.id || `unit-${svgX}-${svgY}`;

        return { unit, position, uniqueId };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, showUnitLabels, viewBox, unitsVersion]);

  // Mirror current overrides into refs so the layout effect can back out the
  // already-applied transform before measuring. Without this, the effect
  // measures the *shifted* icon rect, sees no collision, removes the offset,
  // and then on the next pass the raw rect collides again — the resulting
  // on/off flip-flop trips React's max-update-depth guard.
  const zoneIconOffsetsRef = useRef(zoneIconOffsets);
  zoneIconOffsetsRef.current = zoneIconOffsets;

  // Per-zone orientation history that survives zone unmount (e.g., zooming
  // below the zone's activation threshold). Preserved so returning to the same
  // zoom restores the same label orientation rather than re-picking from
  // scratch and potentially landing on a different side.
  const orientationHistoryRef = useRef({});

  // Resolve zoom-zone label overlaps with place labels by trying alternative
  // orientations. After layout, measure each zone label against all visible
  // place labels; if the current orientation overlaps, pick the first
  // non-colliding orientation (preferring the one configured on the zone).
  // Disabled for birds eye zones (zoomZonesOnFloor=false) per user request.
  useLayoutEffect(() => {
    // Collision detection disabled: the measure → setState → label-class-change
    // → MutationObserver → collisionTick → re-measure cycle fails to converge on
    // some communities (sub-pixel rect jitter / oscillating orientations),
    // tripping React's max-update-depth limit. Re-enable once orientation
    // scoring is reworked to be stable. Birds eye was already skipped.
    return;
    // eslint-disable-next-line no-unreachable
    if (showZoomZones && !zoomZonesOnFloor) return;

    const container = containerRef.current;
    if (!container) return;
    const zoneEls = Array.from(container.querySelectorAll('[data-zone-key]'));
    if (zoneEls.length === 0) return;
    // Place labels are rendered by a sibling MapLabels instance (separate
    // container), so query the whole document rather than just our container.
    // Split obstacles into two groups: dots (used for the icon-offset check)
    // and a combined set (used for label-orientation resolution, where we
    // want to avoid covering either a label or a dot).
    const collect = (selector) => Array.from(document.querySelectorAll(selector))
      .filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0)
      .map((el) => el.getBoundingClientRect());
    const placeDotRects = collect('.htmlPlaceMarker__dot, .htmlClusterBadge__icon');
    // Cluster icons (43px) are big enough to be a real visual obstacle for
    // zone labels, unlike place dots (14px). Tracked separately so we can
    // include them in zone-label hardObstacles below without also re-
    // introducing tiny place dots there.
    const clusterIconRects = collect('.htmlClusterBadge__icon')
      .map((rect) => ({
        left: rect.left - 10,
        top: rect.top - 10,
        right: rect.right + 10,
        bottom: rect.bottom + 10,
        width: rect.width + 20,
        height: rect.height + 20,
      }));
    // Place labels carry data-place-uuid for collision detection tracking.
    // Cluster badge labels lack a uuid and only contribute to the obstacle set.
    // Use the TEXT bounds (via Range.getBoundingClientRect) rather than the
    // full label box. The label has padding and a background that extends
    // past the text — collision against the text bounds means the orientation
    // pass only considers an obstruction when the *text* would actually be
    // covered. Slight label-box overlap is acceptable visually.
    const textRectOf = (el) => {
      const range = document.createRange();
      range.selectNodeContents(el);
      return range.getBoundingClientRect();
    };
    const clusterLabelRects = Array.from(document.querySelectorAll('.htmlClusterBadge__label'))
      .filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0)
      .map((el) => {
        const textRect = textRectOf(el);
        // Add margin around cluster badge text for buffer space
        return {
          left: textRect.left - 8,
          top: textRect.top - 8,
          right: textRect.right + 8,
          bottom: textRect.bottom + 8,
          width: textRect.width + 16,
          height: textRect.height + 16,
        };
      });
    const placeLabelInfo = Array.from(document.querySelectorAll('.htmlPlaceLabel'))
      .filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0 && el.dataset.placeUuid)
      .map((el) => {
        const textRect = textRectOf(el);
        // Add margin around place label text for buffer space
        return {
          uuid: el.dataset.placeUuid,
          rect: {
            left: textRect.left - 6,
            top: textRect.top - 6,
            right: textRect.right + 6,
            bottom: textRect.bottom + 6,
            width: textRect.width + 12,
            height: textRect.height + 12,
          },
        };
      });
    // Unit labels (the "2115/2116/..." numbers on building tiles) are
    // intentionally excluded from BOTH the zone-label and overflow-cluster
    // obstacle sets — they fill most of the building area and would block
    // almost every position. Cluster badge labels and place dots are
    // assembled into the hardObstacles list per-zone in the loop below.

    const overlaps = (a, b) => !(
      a.right <= b.left
      || a.left >= b.right
      || a.bottom <= b.top
      || a.top >= b.bottom
    );

    const rectFor = (iconRect, size, orientation, offset = 10, diagOffset = 4) => {
      const cx = iconRect.left + iconRect.width / 2;
      const cy = iconRect.top + iconRect.height / 2;
      switch (orientation) {
        case 'top':
          return {
            left: cx - size.width / 2,
            right: cx + size.width / 2,
            top: iconRect.top - size.height - offset,
            bottom: iconRect.top - offset,
          };
        case 'bottom':
          return {
            left: cx - size.width / 2,
            right: cx + size.width / 2,
            top: iconRect.bottom + offset,
            bottom: iconRect.bottom + offset + size.height,
          };
        case 'right':
          return {
            left: iconRect.right + offset,
            right: iconRect.right + offset + size.width,
            top: cy - size.height / 2,
            bottom: cy + size.height / 2,
          };
        case 'top-left':
          return {
            left: iconRect.left - size.width - diagOffset,
            right: iconRect.left - diagOffset,
            top: iconRect.top - size.height - diagOffset,
            bottom: iconRect.top - diagOffset,
          };
        case 'top-right':
          return {
            left: iconRect.right + diagOffset,
            right: iconRect.right + diagOffset + size.width,
            top: iconRect.top - size.height - diagOffset,
            bottom: iconRect.top - diagOffset,
          };
        case 'bottom-left':
          return {
            left: iconRect.left - size.width - diagOffset,
            right: iconRect.left - diagOffset,
            top: iconRect.bottom + diagOffset,
            bottom: iconRect.bottom + diagOffset + size.height,
          };
        case 'bottom-right':
          return {
            left: iconRect.right + diagOffset,
            right: iconRect.right + diagOffset + size.width,
            top: iconRect.bottom + diagOffset,
            bottom: iconRect.bottom + diagOffset + size.height,
          };
        case 'left':
        default:
          return {
            left: iconRect.left - size.width - offset,
            right: iconRect.left - offset,
            top: cy - size.height / 2,
            bottom: cy + size.height / 2,
          };
      }
    };

    // Shift an icon rect by (dx, dy) px without mutating the original.
    // Includes width/height so downstream code (findClusterPos, centroid
    // calc) that reads .width / .height doesn't get NaN — getBoundingClientRect
    // returns those, but the spread-style rect we build here didn't.
    const shiftRect = (r, dx, dy) => ({
      left: r.left + dx,
      right: r.right + dx,
      top: r.top + dy,
      bottom: r.bottom + dy,
      width: r.right - r.left,
      height: r.bottom - r.top,
    });

    // If the zone's dot/icon overlaps any clickable pin (other zone icon or
    // cluster icon), shift it away so the two pins don't overlap. Need
    // ~43+px between centers to clear two full-size zoom icons; 50 gives a
    // small buffer. Tries cardinals first (cleaner visual), then diagonals.
    const ICON_SHIFT = 50;
    const DIAG_SHIFT = ICON_SHIFT * 0.7071; // ~35px each axis = ~50px hypot
    const shiftDirections = [
      { dx: 0, dy: ICON_SHIFT },
      { dx: 0, dy: -ICON_SHIFT },
      { dx: ICON_SHIFT, dy: 0 },
      { dx: -ICON_SHIFT, dy: 0 },
      { dx: DIAG_SHIFT, dy: DIAG_SHIFT },
      { dx: -DIAG_SHIFT, dy: DIAG_SHIFT },
      { dx: DIAG_SHIFT, dy: -DIAG_SHIFT },
      { dx: -DIAG_SHIFT, dy: -DIAG_SHIFT },
    ];
    const findIconOffset = (iconRect, obstacles) => {
      const collidesNow = obstacles.some((dr) => overlaps(iconRect, dr));
      if (!collidesNow) return null;
      for (let i = 0; i < shiftDirections.length; i += 1) {
        const { dx, dy } = shiftDirections[i];
        const shifted = shiftRect(iconRect, dx, dy);
        if (!obstacles.some((dr) => overlaps(shifted, dr))) {
          return { dx, dy };
        }
      }
      return { dx: 0, dy: ICON_SHIFT };
    };

    // Cardinals first, diagonals only as fallback when no cardinal is clear.
    const candidates = [
      'bottom', 'top', 'right', 'left',
      'bottom-right', 'bottom-left', 'top-right', 'top-left',
    ];

    // Zone-vs-zone obstacles: each zone's icon/dot acts like a place dot for
    // the icon-shift pass, and its label acts like a place label for the
    // orientation pass. Computed at the base orientation (not the currently-
    // rendered orientation) so the obstacle set doesn't depend on this
    // effect's own output — measuring the rendered rect would let two
    // adjacent zones oscillate orientation choices against each other.
    const appliedOffsets = zoneIconOffsetsRef.current;
    const zoneIconRectsByKey = {};
    const zoneLabelRectsByKey = {};
    const zoneTextRectsByKey = {};
    const zoneBaseOrientationByKey = {};
    const zoneLabelSizeByKey = {};
    zoneEls.forEach((el) => {
      const k = el.dataset.zoneKey;
      const baseOri = el.dataset.zoneOrientation || 'bottom';
      const iconEl = el.querySelector('.htmlZoomZone__icon, .htmlZoomZone__dot');
      // Include labels that are visible OR fade-pending (about to be visible
      // after orientation calc). Only EXCLUDE labels with the --hidden class,
      // which signals the user has minimized an asDot zone — those won't
      // appear and shouldn't act as obstacles. Including fade-pending labels
      // here prevents the race where other zones pick orientations during
      // the brief invisible window and then can't reorient cleanly later.
      const labelEl = el.querySelector(
        '.htmlZoomZone__label:not(.htmlZoomZone__label--hidden)',
      );
      if (!iconEl || iconEl.offsetWidth <= 0 || iconEl.offsetHeight <= 0) return;
      const measured = iconEl.getBoundingClientRect();
      const appliedOffset = appliedOffsets[k];
      const rawIcon = appliedOffset
        ? shiftRect(measured, -appliedOffset.dx, -appliedOffset.dy)
        : measured;
      zoneIconRectsByKey[k] = rawIcon;
      zoneBaseOrientationByKey[k] = baseOri;
      if (labelEl && labelEl.offsetWidth > 0 && labelEl.offsetHeight > 0) {
        // Self size (text bounds via Range) — used by THIS zone's own
        // rectFor calls when scoring its candidate orientations.
        const tr = textRectOf(labelEl);
        zoneLabelSizeByKey[k] = { width: tr.width, height: tr.height };
        zoneTextRectsByKey[k] = tr;
        // Obstacle rect (full label box at current rendered position) —
        // OTHER zones use this to avoid overlapping where this zone's
        // label actually IS, not where it would be at base orientation.
        // Without this, two adjacent zones can each pick orientations
        // that look clean against the other's base position but still
        // overlap when both moves are applied.
        zoneLabelRectsByKey[k] = labelEl.getBoundingClientRect();
      }
    });

    const nextOrientations = {};
    const nextOffsets = {};
    const ZONE_TEXT_BUFFER = 8;
    const containerRect = container.getBoundingClientRect();
    // Ring-search params for the in-footprint zone-marker shift. When a
    // zone label has no clean orientation at its base position, shift the
    // whole marker (icon + label) outward in concentric rings until a
    // candidate position clears all hard obstacles and all place labels.
    const SHIFT_RING_RADII = [60, 110, 170, 240, 320];
    const SHIFT_RING_ANGLES = 12;
    // SVG → container-px conversion for the footprint bbox check. Same math
    // as svgToPercent, then scaled into pixels using the cached containerRect.
    const svgPointToContainerPx = (svgX, svgY) => {
      if (!viewBox?.width || !viewBox?.height) return null;
      const pctX = (svgX - viewBox.x) / viewBox.width;
      const pctY = (svgY - viewBox.y) / viewBox.height;
      return {
        x: containerRect.left + pctX * containerRect.width,
        y: containerRect.top + pctY * containerRect.height,
      };
    };
    zoneEls.forEach((zoneEl) => {
      const key = zoneEl.dataset.zoneKey;
      const baseOrientation = zoneBaseOrientationByKey[key] || 'bottom';
      const rawIconRect = zoneIconRectsByKey[key];
      if (!rawIconRect) return;

      // Per-zone obstacle lists exclude self so a zone never collides with
      // its own icon/label.
      const otherZoneIconRects = Object.entries(zoneIconRectsByKey)
        .filter(([k]) => k !== key)
        .map(([k, r]) => {
          const offset = appliedOffsets[k];
          return offset ? shiftRect(r, offset.dx, offset.dy) : r;
        });
      const otherZoneLabelRects = Object.entries(zoneLabelRectsByKey)
        .filter(([k]) => k !== key)
        .map(([, r]) => r);
      const dotObstacles = [...placeDotRects, ...otherZoneIconRects];
      // Hard obstacles: must be avoided entirely. Place dots (with or
      // without "+") are intentionally excluded — they're small and the
      // zone label legitimately needs to sit over them in dense areas.
      // Place labels are SOFT — they're scored separately below.
      const hardObstacles = [
        ...clusterIconRects,
        ...clusterLabelRects,
        ...otherZoneIconRects,
        ...otherZoneLabelRects,
        ...placeLabelInfo.map((info) => info.rect),
      ];

      // Step 1: shift the whole zoom zone if its raw position overlaps a
      // place dot or another zone's icon. Collision is computed against the
      // unshifted rect (see appliedOffsets back-out above) so an already-
      // applied transform doesn't mask the collision it's clearing.
      const offset = findIconOffset(rawIconRect, dotObstacles);
      if (offset) nextOffsets[key] = offset;
      const iconRect = offset ? shiftRect(rawIconRect, offset.dx, offset.dy) : rawIconRect;

      // Step 2: pick a label orientation that avoids all obstacles using the
      // (possibly shifted) icon position as anchor.
      const size = zoneLabelSizeByKey[key];
      if (!size) {
        // Hidden label (asDot minimized): preserve the prior orientation
        // and icon offset so the label fades in place. Without this the
        // cleanup loops below drop the entries, the wrapper reverts to
        // base orientation mid-fade, and the label appears to slide.
        const stickyOri = orientationHistoryRef.current[key];
        if (stickyOri && stickyOri !== baseOrientation) {
          nextOrientations[key] = stickyOri;
        }
        if (appliedOffsets[key]) {
          nextOffsets[key] = appliedOffsets[key];
        }
        return;
      }

      // Sticky overflow logic removed — zoom zones now always try to find
      // clean orientations or accept overlap, never hiding place labels.

      const tryOrder = [
        baseOrientation,
        ...candidates.filter((o) => o !== baseOrientation),
      ];

      // Score every orientation that clears hard obstacles by zone overlaps, then place covers, then distance-to-own-pin
      let bestOrientation = null;
      let bestCovered = null;
      let bestZoneOverlap = Infinity;
      let bestDistToOwnPin = Infinity;

      const iconCenterX = iconRect.left + iconRect.width / 2;
      const iconCenterY = iconRect.top + iconRect.height / 2;

      for (let i = 0; i < tryOrder.length; i += 1) {
        const candidate = tryOrder[i];
        const candidateRect = rectFor(iconRect, size, candidate);
        if (hardObstacles.some((pr) => overlaps(candidateRect, pr))) continue;

        const covered = placeLabelInfo.filter((info) => overlaps(candidateRect, info.rect));

        // Count zone overlaps using TEXT rects with 8px buffer (more accurate than full label box)
        let zoneOverlapCount = 0;
        Object.entries(zoneTextRectsByKey)
          .filter(([k]) => k !== key)
          .forEach(([, textRect]) => {
            if (!textRect) return;
            const textRectBuffered = {
              left: textRect.left - ZONE_TEXT_BUFFER,
              top: textRect.top - ZONE_TEXT_BUFFER,
              right: textRect.right + ZONE_TEXT_BUFFER,
              bottom: textRect.bottom + ZONE_TEXT_BUFFER,
            };
            if (overlaps(candidateRect, textRectBuffered)) {
              zoneOverlapCount += 1;
            }
          });

        // Calculate distance to own pin (tiebreaker)
        const labelCenterX = candidateRect.left + candidateRect.width / 2;
        const labelCenterY = candidateRect.top + candidateRect.height / 2;
        const distToOwnPin = Math.sqrt((labelCenterX - iconCenterX) ** 2 + (labelCenterY - iconCenterY) ** 2);

        // Prefer: fewer zone overlaps >> fewer place covers >> closer to own pin
        const isStrictlyBetter = bestOrientation === null
          || zoneOverlapCount < bestZoneOverlap
          || (zoneOverlapCount === bestZoneOverlap && covered.length < bestCovered.length)
          || (zoneOverlapCount === bestZoneOverlap && covered.length === bestCovered.length && distToOwnPin < bestDistToOwnPin);

        if (isStrictlyBetter) {
          bestOrientation = candidate;
          bestCovered = covered;
          bestZoneOverlap = zoneOverlapCount;
          bestDistToOwnPin = distToOwnPin;
        }
      }

      // Pick orientation by zone overlap count, with place covers and distance as tiebreakers
      let finalOrientation;
      let finalCovered;
      const historical = orientationHistoryRef.current[key];
      let historicalCovered = null;
      let historicalDistToOwnPin = Infinity;

      if (historical) {
        const historicalRect = rectFor(iconRect, size, historical);
        if (!hardObstacles.some((pr) => overlaps(historicalRect, pr))) {
          historicalCovered = placeLabelInfo.filter(
            (info) => overlaps(historicalRect, info.rect),
          );
          const labelCenterX = historicalRect.left + historicalRect.width / 2;
          const labelCenterY = historicalRect.top + historicalRect.height / 2;
          historicalDistToOwnPin = Math.sqrt((labelCenterX - iconCenterX) ** 2 + (labelCenterY - iconCenterY) ** 2);
        }
      }

      const bestCount = bestCovered?.length ?? Infinity;
      const historicalCount = historicalCovered?.length ?? Infinity;

      // Use historical if it has same or fewer place covers, with distance as final tiebreak
      if (historicalCovered && (historicalCount < bestCount || (historicalCount === bestCount && historicalDistToOwnPin < bestDistToOwnPin))) {
        finalOrientation = historical;
        finalCovered = historicalCovered;
      } else if (bestOrientation) {
        finalOrientation = bestOrientation;
        finalCovered = bestCovered;
      } else {
        // No clean orientation found. Use base orientation.
        finalOrientation = baseOrientation;
        finalCovered = placeLabelInfo.filter(
          (info) => overlaps(rectFor(iconRect, size, baseOrientation), info.rect),
        );
      }
      orientationHistoryRef.current[key] = finalOrientation;
      if (finalOrientation !== baseOrientation) nextOrientations[key] = finalOrientation;

      // Update this zone's entry in zoneLabelRectsByKey with the new
      // chosen-orientation rect. Subsequent zones in this iteration see the
      // updated position, so they can coordinate (e.g., if Lodge picks
      // top-left, Cottage iterating next sees Lodge at top-left and avoids
      // it). Without this, each zone optimizes against initial positions
      // and the picks can end up overlapping each other.
      zoneLabelRectsByKey[key] = rectFor(iconRect, size, finalOrientation);
      zoneTextRectsByKey[key] = rectFor(iconRect, size, finalOrientation);

      if (finalCovered.length === 0) {
        // Clean orientation found — no overflow needed.
        return;
      }

      // Cluster absorption removed — covered places stay visible under the
      // zone label instead of being hidden and absorbed into clusters.

      // Step 3: try to shift the whole zone marker (icon + label) within the
      // zone's building footprint to a position where some orientation clears
      // all hard obstacles AND all place labels. Ring search from current
      // icon center outward; first clean hit wins.
      const zoneUuid = key.replace(/^zone:/, '');
      const footprint = zoneFootprints?.get(zoneUuid);
      if (!footprint) return; // no footprint data → accept overlap
      const fpTL = svgPointToContainerPx(footprint.minX, footprint.minY);
      const fpBR = svgPointToContainerPx(footprint.maxX, footprint.maxY);
      if (!fpTL || !fpBR) return;
      const iconCx = iconRect.left + iconRect.width / 2;
      const iconCy = iconRect.top + iconRect.height / 2;
      let shiftFound = null;
      for (let ri = 0; ri < SHIFT_RING_RADII.length && !shiftFound; ri += 1) {
        const radius = SHIFT_RING_RADII[ri];
        for (let ai = 0; ai < SHIFT_RING_ANGLES && !shiftFound; ai += 1) {
          const angle = (ai * Math.PI * 2) / SHIFT_RING_ANGLES;
          const dx = Math.cos(angle) * radius;
          const dy = Math.sin(angle) * radius;
          const candCx = iconCx + dx;
          const candCy = iconCy + dy;
          if (candCx < fpTL.x || candCx > fpBR.x) continue;
          if (candCy < fpTL.y || candCy > fpBR.y) continue;
          const candIcon = shiftRect(iconRect, dx, dy);
          if (dotObstacles.some((d) => overlaps(candIcon, d))) continue;
          for (let oi = 0; oi < tryOrder.length; oi += 1) {
            const ori = tryOrder[oi];
            const candLabel = rectFor(candIcon, size, ori);
            if (hardObstacles.some((h) => overlaps(candLabel, h))) continue;
            if (placeLabelInfo.some((info) => overlaps(candLabel, info.rect))) continue;
            shiftFound = { dx, dy, orientation: ori };
            break;
          }
        }
      }
      if (shiftFound) {
        // Combine with the step-1 icon offset (also stored in nextOffsets).
        const combined = offset
          ? { dx: offset.dx + shiftFound.dx, dy: offset.dy + shiftFound.dy }
          : { dx: shiftFound.dx, dy: shiftFound.dy };
        nextOffsets[key] = combined;
        if (shiftFound.orientation === baseOrientation) {
          delete nextOrientations[key];
        } else {
          nextOrientations[key] = shiftFound.orientation;
        }
        // Update zoneLabelRectsByKey so later zones see the shifted position.
        zoneLabelRectsByKey[key] = rectFor(
          shiftRect(iconRect, shiftFound.dx, shiftFound.dy),
          size,
          shiftFound.orientation,
        );
        return;
      }
      // Shift failed too — accept that the zone label visually covers some
      // place labels at the chosen orientation. No overflow registered: the
      // covered labels stay visible (per spec: visible-but-overlapped beats
      // silent disappearance when there's no badge to point them out).
    });

    // PASS 2 disabled — 8px buffer causing false-positive overlaps
    // Pass 1 (orientation scoring with zone overlap prioritization) handles most cases well

    // Zone-vs-cluster overlap detection removed — clusters stay visible even
    // if covered by zone labels.

    setZoneOrientationOverrides((prev) => {
      const merged = { ...prev };
      let changed = false;
      Object.entries(nextOrientations).forEach(([k, v]) => {
        if (merged[k] !== v) { merged[k] = v; changed = true; }
      });
      Object.keys(merged).forEach((k) => {
        if (!(k in nextOrientations) && k.startsWith('zone:')) {
          delete merged[k];
          changed = true;
        }
      });
      return changed ? merged : prev;
    });

    setZoneIconOffsets((prev) => {
      const merged = { ...prev };
      let changed = false;
      Object.entries(nextOffsets).forEach(([k, v]) => {
        const existing = merged[k];
        if (!existing || existing.dx !== v.dx || existing.dy !== v.dy) {
          merged[k] = v;
          changed = true;
        }
      });
      Object.keys(merged).forEach((k) => {
        if (!(k in nextOffsets) && k.startsWith('zone:')) {
          delete merged[k];
          changed = true;
        }
      });
      return changed ? merged : prev;
    });

    // setOverflowZones removed — overflow clustering disabled, zone labels
    // just cover place labels directly without hiding them.

  // visibleLabels intentionally omitted — only zone visibility matters for
  // the collision pass, captured via the zoneVisibilitySignature string.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomZones, placesToRender, viewBox, units, zoneVisibilitySignature, clusters,
    zoneFootprints, collisionTick, showZoomZones, zoomZonesOnFloor]);

  // Cluster absorption, boosts, and covered-place-hiding all removed —
  // zone labels just cover place labels directly without hiding them or
  // modifying cluster counts.

  // re-run when zones appear/disappear or toggle expansion. Debounced to one
  // re-run per frame. We intentionally do NOT watch place label / dot /
  // place-cluster mutations — per design, opening/closing individual place
  // labels and toggling the global markers visibility shouldn't recalc the
  // overflow clusters (the user accepts that minor obstacle changes from
  // those toggles won't shift the zone-level layout).
  useEffect(() => {
    // Disabled alongside the collision pass above — this observer only existed
    // to bump collisionTick and re-run that (now no-op) pass.
    return undefined;
    // eslint-disable-next-line no-unreachable
    if (typeof MutationObserver === 'undefined') return undefined;
    let pendingFrame = null;
    const schedule = () => {
      if (pendingFrame !== null) return;
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        setCollisionTick((t) => t + 1);
      });
    };
    const observer = new MutationObserver((mutations) => {
      const labelLike = (node) => node?.classList
        && node.classList.contains('htmlZoomZone__label');
      const relevant = mutations.some((m) => {
        if (m.type === 'childList') {
          return [...m.addedNodes, ...m.removedNodes].some(labelLike);
        }
        if (m.type === 'attributes') {
          return labelLike(m.target);
        }
        return false;
      });
      if (relevant) schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => {
      observer.disconnect();
      if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);
    };
  }, []);

  // Container styles - must have explicit dimensions for percentage-based positioning
  const containerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex,
    pointerEvents: 'none', // Allow clicks to pass through to SVG below
    overflow: 'visible', // Allow labels to extend beyond bounds
  };

  return (
    <div className="mapLabels" style={containerStyle} ref={containerRef}>

      {/* Render unit labels (bottom layer - behind everything) */}
      {unitLabelPositions.map(({ unit, position, uniqueId }) => (
        <MapUnitLabel
          key={uniqueId}
          unit={unit}
          position={position}
          isFavorited={favoritedUnitUuids.has(unit.info?.uuid)}
          showHeartEnabled={showHeartEnabled}
          showOccupancyInLabel={platformShowOccupancy}
          activeZoom={activeZoom}
          baseZoom={baseZoom}
        />
      ))}

      {/* Render zoom zones */}
      {zoomZonePositions.map(({ zoomZone, position }) => {
        const zoneKey = `zone:${zoomZone.uuid || zoomZone.id}`;
        // --asDot zones default to visible; user toggle stored in visibleLabels.
        // Non-asDot zones always show their label (full zoom navigation hint).
        const labelVisible = !zoomZone.has_active_content
          || visibleLabels[zoneKey] !== false;
        const orientationOverride = zoneOrientationOverrides[zoneKey];
        const iconOffset = zoneIconOffsets[zoneKey];
        return (
          <MapZoomZone
            key={zoomZone.id}
            zoneKey={zoneKey}
            zoomZone={zoomZone}
            position={position}
            onZoomZoneClick={onZoomZoneClick}
            isPulsing={isZoomZonesGuideActive}
            isOnFloor={zoomZonesOnFloor}
            labelVisible={labelVisible}
            onToggleLabel={() => toggleLabel(zoneKey)}
            orientationOverride={orientationOverride}
            iconOffset={iconOffset}
          />
        );
      })}

      {/* Render place markers */}
      {markerPositions.map(({
        place, position, isLink, uniqueId,
      }) => (
        <MapPlaceLabel
          key={uniqueId}
          uniqueId={uniqueId}
          place={place}
          position={position}
          isLink={isLink}
          labelVisible={isLink || visibleLabels[uniqueId]}
          globalLabelVisible={showPlaceLabels}
          onToggleLabel={toggleLabel}
          onMarkerClick={onMarkerClick}
          isFavorited={favoritedPlaceUuids.has(place.pin?.uuid || place.uuid)}
          showHeartEnabled={showHeartEnabled}
          isPulsing={isAmenitiesGuideActive}
          isPreviewMode={isPreviewMode}
        />
      ))}

      {/* Render cluster badges (top layer) */}
      {clusterPositions.map(({ cluster, position }) => {
        const boostUuids = clusterBoosts?.get(cluster.id);
        return (
          <MapPlaceCluster
            key={cluster.id}
            cluster={cluster}
            position={position}
            onClusterClick={onClusterClick}
            globalLabelVisible={showPlaceLabels}
            boostCount={boostUuids ? boostUuids.length : 0}
          />
        );
      })}

    </div>
  );
};

export default MapLabels;
